import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_CONSTANT, EntityTypes, Status } from '@guryou/core';
import { dateIsAfter, dateIsBefore, printDate, printPrice, printTime } from '@guryou/utils';
import { useTheme } from '@material-ui/core/styles';
import Modal from '../../components/shared/Modal';
import Sessions from './Sessions';

const AllReservationsTable = ({ status, view, obj }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [isSessionsModalOpen, setIsSessionsModalOpen] = useState(false);
  const itemsPerPage = 10;

  //eslint-disable-next-line
  let data = obj.data.filter(x => view === ALL_CONSTANT || view === x.type);

  data =
    status === Status.upcoming
      ? data.filter(x => x.type === EntityTypes.video || dateIsAfter(x.startTime))
      : data.filter(x => x.type === EntityTypes.video || dateIsBefore(x.startTime));

  const findItem = value => {
    return obj.data.find(x => x.id === value);
  };

  const handleGroupColor = value => {
    return value ? `#${value}` : theme.palette.primary.main;
  };

  const handleDuration = value => {
    const item = findItem(value);
    if (!item || item.type === EntityTypes.video) {
      return '/';
    }
    return `${item.duration} ${item.timeUnit}s`;
  };

  const handleModal = value => {
    const item = findItem(value);
    setSelectedService(item);
    setIsSessionsModalOpen(true);
  };

  const closeSessionsModal = () => {
    setIsSessionsModalOpen(false);
    setSelectedService(null);
  };

  // Pagination
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data.slice(startIndex, endIndex);

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">{t('RESERVATIONS.noReservations')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('GENERAL.group')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('GENERAL.type')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('GENERAL.name')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('GENERAL.price')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.date')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.time')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.duration')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.resource')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('SESSIONS.titlePlural')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((item, index) => (
              <tr key={item.id || index} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: handleGroupColor(item.color) }}></div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.type || '/'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name || t('GENERAL.noName')}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{printPrice(item.price)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.date ? printDate(item.date) : '/'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.date ? printTime(item.date) : '/'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{handleDuration(item.id)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.resourceName || '/'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {item.sessions ? (
                    <button onClick={() => handleModal(item.id)} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
                      {t('SESSIONS.titlePlural')}
                    </button>
                  ) : (
                    '/'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            {t('GENERAL.showing', 'Showing')} {startIndex + 1} {t('GENERAL.to', 'to')} {Math.min(endIndex, data.length)} {t('GENERAL.of', 'of')}{' '}
            {data.length} {t('GENERAL.results', 'results')}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('GENERAL.previous', 'Previous')}
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('GENERAL.next', 'Next')}
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isSessionsModalOpen}
        onClose={closeSessionsModal}
        title={selectedService ? `${selectedService.name} (${t('SESSIONS.titlePlural')})` : t('SESSIONS.titlePlural')}
      >
        <div className="p-6">
          <Sessions service={selectedService} />
        </div>
      </Modal>
    </>
  );
};

export default AllReservationsTable;
